import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Scan, CheckCircle, XCircle, Loader2, Users } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function JoinTeam({ onNavigateHome }) {
  const [status, setStatus] = useState('loading'); // loading | success | error | login_required
  const [message, setMessage] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    if (!token) { setStatus('error'); setMessage('Token de invitación no encontrado.'); return; }

    const authToken = localStorage.getItem('veritas_token');
    axios.post(
      `${API_URL}/api/team/accept?token=${token}`,
      {},
      { headers: authToken ? { Authorization: `Bearer ${authToken}` } : {} }
    )
      .then(r => {
        setInviteEmail(r.data.email || '');
        setStatus('success');
      })
      .catch(err => {
        const detail = err.response?.data?.detail || 'Error al aceptar la invitación.';
        if (err.response?.status === 403 && detail.includes('otro email')) {
          setStatus('login_required');
          setMessage(detail);
        } else {
          setStatus('error');
          setMessage(detail);
        }
      });
  }, []);

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center px-4">
      <div className="fixed inset-0 pointer-events-none" style={{ background: 'radial-gradient(circle at 50% 30%, rgba(147,51,234,0.1), transparent 60%)' }} />

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-10 w-full max-w-md bg-zinc-900 border border-zinc-800 rounded-2xl p-8 text-center space-y-6"
      >
        {/* Logo */}
        <div className="flex items-center justify-center gap-2 mb-2">
          <Scan className="w-5 h-5 text-blue-500" />
          <span className="font-heading text-lg font-black">VERITAS <span className="text-blue-500">SCAN</span></span>
        </div>

        {status === 'loading' && (
          <div className="space-y-4">
            <Loader2 className="w-12 h-12 text-purple-500 animate-spin mx-auto" />
            <p className="text-zinc-400 font-mono text-sm">Procesando invitación...</p>
          </div>
        )}

        {status === 'success' && (
          <div className="space-y-4">
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 200, damping: 15 }}>
              <div className="w-16 h-16 bg-purple-500/10 rounded-full flex items-center justify-center mx-auto">
                <Users className="w-8 h-8 text-purple-400" />
              </div>
            </motion.div>
            <div>
              <h1 className="font-heading text-2xl font-black text-white mb-2">¡Bienvenido al equipo!</h1>
              <p className="text-zinc-400 text-sm leading-relaxed">
                Tu cuenta <span className="text-purple-400 font-mono">{inviteEmail}</span> ahora tiene acceso al plan Business.
              </p>
            </div>
            <button
              onClick={onNavigateHome}
              className="w-full py-3 bg-purple-600 hover:bg-purple-500 text-white font-mono text-sm font-bold uppercase tracking-wider rounded-xl transition-all"
            >
              Ir a Veritas Scan →
            </button>
          </div>
        )}

        {status === 'error' && (
          <div className="space-y-4">
            <XCircle className="w-12 h-12 text-red-400 mx-auto" />
            <div>
              <h1 className="font-heading text-xl font-black text-white mb-2">Invitación inválida</h1>
              <p className="text-zinc-500 text-sm">{message}</p>
            </div>
            <button onClick={onNavigateHome} className="text-blue-400 hover:text-blue-300 font-mono text-sm transition-colors">
              Volver al inicio
            </button>
          </div>
        )}

        {status === 'login_required' && (
          <div className="space-y-4">
            <div className="w-16 h-16 bg-amber-500/10 rounded-full flex items-center justify-center mx-auto">
              <Users className="w-8 h-8 text-amber-400" />
            </div>
            <div>
              <h1 className="font-heading text-xl font-black text-white mb-2">Inicia sesión primero</h1>
              <p className="text-zinc-400 text-sm leading-relaxed">{message}</p>
            </div>
            <button
              onClick={onNavigateHome}
              className="w-full py-3 bg-amber-600 hover:bg-amber-500 text-white font-mono text-sm font-bold uppercase tracking-wider rounded-xl transition-all"
            >
              Ir a iniciar sesión
            </button>
          </div>
        )}
      </motion.div>
    </div>
  );
}
